import Flex from 'components/ui/Flex';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';
import Pagination from 'components/ui/Pagination';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    searchBatchReturns,
    selectBatchSearchResults,
    selectBatchSearchPageInfo,
    selectBatchSearchLoading,
    setBatchCardLoading,
} from 'store/reducers/batchReturnSlice';
import { setSelectedBatchStatus } from 'store/reducers/navigationSlice';
import { getBatchStatusInfo, parseBatchStatusFromUrl } from 'utils/helpers';
import useBreakPoints from 'hooks/useBreakPoints';
import BatchReturnTable, { BatchReturnOrderByField } from './BatchReturnTable';
import { BatchReturnOverview } from 'types/batchReturn';
import NoResultsFound from 'components/ui/NoResultsFound';
import { BATCH_BP } from './BatchReturn';

const SEARCH_PAGE_SIZE = 10;

const BatchReturnStatus = () => {
    const dispatch = useAppDispatch();
    const { status } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const bp = useBreakPoints();

    const compact = bp.width < 900;

    const results = useAppSelector(selectBatchSearchResults);
    const pageInfo = useAppSelector(selectBatchSearchPageInfo);
    const loading = useAppSelector(selectBatchSearchLoading);

    const [orderBy, setOrderBy] = useState<string>('Id');
    const [orderByDirection, setOrderByDirection] = useState<string>('DESC');

    const statusType = parseBatchStatusFromUrl(status);
    const statusInfo = getBatchStatusInfo(status, t);

    useEffect(() => {
        if (statusType !== undefined) {
            dispatch(setSelectedBatchStatus(statusType));
            dispatch(setBatchCardLoading({ status: String(statusType), isLoading: true }));
            dispatch(
                searchBatchReturns({
                    page: 1,
                    size: SEARCH_PAGE_SIZE,
                    status: statusType,
                    orderBy: 'Id',
                    orderByDirection: 'DESC',
                }),
            );
        }
    }, [dispatch, statusType]);

    const handleSort = useCallback(
        (field: BatchReturnOrderByField) => {
            const isSameField = orderBy === field;
            const newDirection = isSameField && orderByDirection === 'DESC' ? 'ASC' : 'DESC';
            setOrderBy(field);
            setOrderByDirection(newDirection);
            dispatch(
                searchBatchReturns({
                    page: pageInfo?.currentPage ?? 1,
                    size: SEARCH_PAGE_SIZE,
                    status: statusType,
                    orderBy: field,
                    orderByDirection: newDirection,
                }),
            );
        },
        [dispatch, orderBy, orderByDirection, statusType, pageInfo],
    );

    const handlePageChange = useCallback(
        (page: number) => {
            dispatch(
                searchBatchReturns({
                    page,
                    size: SEARCH_PAGE_SIZE,
                    status: statusType,
                    orderBy,
                    orderByDirection,
                }),
            );
        },
        [dispatch, statusType, orderBy, orderByDirection],
    );

    const handleRowClick = useCallback(
        (item: BatchReturnOverview) => {
            navigate(`/batch-returns/${status}/${item.id}`);
        },
        [navigate, status],
    );

    if (statusType === undefined) {
        return <NoResultsFound />;
    }

    return (
        <Flex column gap="1rem" $grow={1} className={`batch-return-status ${bp.width < BATCH_BP ? 'p-3' : ''}`}>
            <Text as="h2" fontSize={32} lineheight={20} bold>
                {statusInfo.heading}
            </Text>

            {loading ? (
                <Spinner />
            ) : results.length === 0 ? (
                <NoResultsFound />
            ) : (
                <>
                    <BatchReturnTable
                        items={results}
                        compact={compact}
                        orderBy={orderBy}
                        orderByDirection={orderByDirection}
                        onSort={handleSort}
                        onRowClick={handleRowClick}
                    />
                    {pageInfo && (
                        <Pagination
                            page={pageInfo.currentPage}
                            pages={pageInfo.totalPages}
                            pageSize={pageInfo.pageSize}
                            itemCount={results.length}
                            itemsTotal={pageInfo.totalItems}
                            label={''}
                            setPage={handlePageChange}
                            compact={false}
                        />
                    )}
                </>
            )}
        </Flex>
    );
};

export default BatchReturnStatus;
